import apiService from './apiService';
import { GET } from './executer';

/**
 * File entry in radar bucket
 */
export interface RadarBucketFile {
  name: string;
  path: string;
  timeCreated: string;
  /** For tiles: whether the file has a row in radar_tiles (ingested) */
  ingested?: boolean;
}

/**
 * Radar input bucket status response
 */
export interface RadarInputStatus {
  bucket: string;
  bucketLabel: string;
  latestFile: string | null;
  path: string | null;
  timeCreated: string | null;
  ageSeconds?: number;
  latestFiles: RadarBucketFile[];
  isDelayed: boolean;
  message: string;
}

/**
 * Expected file status in output bucket (L2 combined NetCDF)
 */
export interface RadarOutputFileStatus {
  name: string;
  path: string;
  type: 'netcdf';
  exists: boolean;
  timeCreated: string | null;
}

/**
 * Expected file status in tiles bucket (realtime or nowcasting)
 */
export interface RadarTilesFileStatus {
  name: string;
  path: string;
  type: string;
  exists: boolean;
  timeCreated: string | null;
  /** Whether the file has a row in radar_tiles (ingested) */
  ingested?: boolean;
}

/**
 * Radar PNG tiles bucket status response
 */
export interface RadarTilesStatus {
  bucket: string;
  bucketLabel: string;
  pathPrefix: string;
  expectedMinute: string;
  expectedRealtimeFile: string;
  expectedNowcastingFiles: string[];
  files: RadarTilesFileStatus[];
  latestFiles: RadarBucketFile[];
  isDelayed: boolean;
  message: string;
}

/**
 * Radar output bucket status response
 */
export interface RadarOutputStatus {
  bucket: string;
  bucketLabel: string;
  pathPrefix: string;
  expectedMinute: string;
  expectedFile: string;
  files: RadarOutputFileStatus[];
  latestFiles: RadarBucketFile[];
  isDelayed: boolean;
  message: string;
}

class RadarStatusService {
  /**
   * Get latest file status for radar input (L2A) bucket.
   * Requires root user.
   */
  async getInputStatus(token: string): Promise<RadarInputStatus> {
    const response = await apiService.client.execute<RadarInputStatus>(
      GET,
      '/api/radar-status/input',
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.errorData?.message || 'Failed to fetch radar input status');
  }

  /**
   * Get status for radar output (NetCDF) bucket - checks for combined L2 NetCDF file.
   * Requires root user.
   */
  async getOutputStatus(token: string): Promise<RadarOutputStatus> {
    const response = await apiService.client.execute<RadarOutputStatus>(
      GET,
      '/api/radar-status/output',
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.errorData?.message || 'Failed to fetch radar output status');
  }

  /**
   * Get status for radar PNG tiles bucket. Requires event to be selected.
   * Requires root user.
   */
  async getTilesStatus(
    token: string,
    seasonYear: number,
    circuitId: string,
    startDate: string
  ): Promise<RadarTilesStatus> {
    const queryParams = new URLSearchParams({
      season_year: String(seasonYear),
      circuit_id: circuitId,
      start_date: startDate,
    });
    const response = await apiService.client.execute<RadarTilesStatus>(
      GET,
      `/api/radar-status/tiles?${queryParams.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.errorData?.message || 'Failed to fetch radar tiles status');
  }
}

export const radarStatusService = new RadarStatusService();
export default radarStatusService;
